import { LinearProgress } from '@mui/joy';

import { Dispatch, SetStateAction } from 'react';

import { useCommit, useRepo } from '@/hooks/useSwr';
import { EPromptType, RunSchema } from '@/types/schema';
import {
  convertOpenAIMessageToStoredMessage,
  isOpenAIMessage,
} from '@/utils/messages';
import { maybeGetOpenAIResponsesInputMessages } from '@/utils/messages';

import { FlatRun } from '../Run/utils/utils';
import { MODEL_SECRETS } from './Playground.constants';
import { PlaygroundInputSourceType } from './PlaygroundContext';
import { PlaygroundMain } from './PlaygroundHome/PlaygroundMain';
import {
  constructInitialOptions,
  createPromptManifestFromRun,
  defaultChatModelManifest,
  defaultCompletionModelManifest,
  escapedRun,
  getDefaultModelManifest,
  getModelForPrompt,
  optionsToOpenAIToolFormat,
  usePlaygroundSerializedWithFallback,
} from './utils/Playground.utils';
import { extractMessagesFromRuns } from './utils/extract_messages';

function extractMessages(run: RunSchema, history?: FlatRun[]) {
  let messages = isOpenAIMessage(run.inputs)
    ? [run.inputs]
    : maybeGetOpenAIResponsesInputMessages(run.inputs) ??
      run.inputs?.messages ??
      run.inputs?.input;

  // History includes the current run
  if (history !== undefined && history.length > 0) {
    messages = extractMessagesFromRuns(history);
    if (run.outputs != null && run.run_type === 'llm') {
      // Omit final response, leave that as run output since we fetch all messages from LLM runs
      messages = messages.slice(0, -1);
    }
  }

  if (!Array.isArray(messages)) {
    return undefined;
  }

  const maybeConvertedOpenAIMessages = messages?.every(isOpenAIMessage)
    ? messages?.map(convertOpenAIMessageToStoredMessage)
    : messages;

  return maybeConvertedOpenAIMessages;
}

// this component is used when the user accesses the playground from a trace
// Projects -> Runs -> [click on a run] -> Playground button
export function RunsPlayground(props: {
  run: RunSchema;
  history?: FlatRun[];
  isSimplified?: boolean;
  scrollRef?: Dispatch<SetStateAction<HTMLDivElement | null>>;
}) {
  const isChat =
    props.run.inputs?.messages != null || isOpenAIMessage(props.run.inputs);

  const defaultModel = isChat
    ? defaultChatModelManifest
    : defaultCompletionModelManifest;

  const model = usePlaygroundSerializedWithFallback(props.run);

  const {
    lc_hub_owner: promptOwner,
    lc_hub_repo: promptName,
    lc_hub_commit_hash: promptCommitHash,
  } = props.run.extra?.metadata ?? {};

  const { data: promptData, isLoading: isLoadingPrompt } = useRepo(
    promptOwner,
    promptName
  );

  const { data: commitData, isLoading: isLoadingCommit } = useCommit({
    owner: promptOwner,
    repo: promptName,
    sha: promptCommitHash,
    include_model: true,
  });

  const isLoadingPromptInformation = isLoadingPrompt || isLoadingCommit;

  // run_type === 'llm' means the run does not have a prompt template associated with it (it's just raw messages)
  if (props.run?.run_type === 'llm') {
    const initialOptions = optionsToOpenAIToolFormat(
      constructInitialOptions(props.run)
    );

    const providerIdInSecrets = model && model.id.join('/') in MODEL_SECRETS;

    const maybeConvertedOpenAIMessages = extractMessages(
      props.run,
      props.history
    );

    const formattedRun = {
      ...props.run,
      inputs: {
        ...props.run.inputs,
        messages: maybeConvertedOpenAIMessages,
      },
    };

    return formattedRun.inputs?.messages != null ? (
      <PlaygroundMain
        key={formattedRun.id}
        promptManifest={createPromptManifestFromRun(
          escapedRun(formattedRun),
          EPromptType.CHAT
        )}
        modelManifest={getDefaultModelManifest(
          providerIdInSecrets ? model ?? defaultModel : defaultModel
        )}
        type={EPromptType.CHAT}
        options={initialOptions}
        run={formattedRun}
        inputSourceType={PlaygroundInputSourceType.MANUAL}
        isSimplified={props.isSimplified}
        // for now (for simplicity), we always (re-)initialize the playground state if open a simplified version of the playground
        forceReset={props.isSimplified}
        scrollRef={props.scrollRef}
      />
    ) : (
      <PlaygroundMain
        key={formattedRun.id}
        promptManifest={createPromptManifestFromRun(
          escapedRun(formattedRun),
          EPromptType.INSTRUCT
        )}
        modelManifest={getDefaultModelManifest(
          providerIdInSecrets ? model ?? defaultModel : defaultModel
        )}
        type={EPromptType.INSTRUCT}
        options={initialOptions}
        run={formattedRun}
        inputSourceType={PlaygroundInputSourceType.MANUAL}
        isSimplified={props.isSimplified}
        // for now (for simplicity), we always (re-)initialize the playground state if open a simplified version of the playground
        forceReset={props.isSimplified}
        scrollRef={props.scrollRef}
      />
    );
  } else if (props.run?.run_type === 'prompt' && props.run.serialized) {
    const modelManifest = getModelForPrompt(
      commitData?.manifest || props.run.serialized
    ).modelManifest;

    if (isLoadingPromptInformation) {
      return (
        <div className="px-4 pt-3">
          <LinearProgress />
        </div>
      );
    }

    return (
      <PlaygroundMain
        key={props.run.id}
        promptManifest={props.run.serialized}
        type={
          props.run.name === 'ChatPromptTemplate'
            ? EPromptType.CHAT
            : EPromptType.INSTRUCT
        }
        modelManifest={modelManifest}
        options={constructInitialOptions(props.run)}
        run={props.run}
        inputSourceType={PlaygroundInputSourceType.MANUAL}
        scrollRef={props.scrollRef}
        repo={promptData?.repo}
        commitHash={commitData?.commit_hash}
      />
    );
  } else if (
    props.run?.run_type === 'chain' &&
    ((Array.isArray(props.run.inputs?.messages) &&
      props.run.inputs.messages.every(isOpenAIMessage)) ||
      isOpenAIMessage(props.run.inputs))
  ) {
    const initialOptions = optionsToOpenAIToolFormat(
      constructInitialOptions(props.run)
    );

    const providerIdInSecrets = model && model.id.join('/') in MODEL_SECRETS;

    const maybeConvertedOpenAIMessages = extractMessages(
      props.run,
      props.history
    );

    const formattedRun = {
      ...props.run,
      inputs: {
        messages: maybeConvertedOpenAIMessages,
      },
    };

    return (
      <PlaygroundMain
        key={formattedRun.id}
        promptManifest={createPromptManifestFromRun(
          escapedRun(formattedRun),
          EPromptType.CHAT
        )}
        modelManifest={getDefaultModelManifest(
          providerIdInSecrets ? model ?? defaultModel : defaultModel
        )}
        type={EPromptType.CHAT}
        options={initialOptions}
        run={formattedRun}
        inputSourceType={PlaygroundInputSourceType.MANUAL}
        scrollRef={props.scrollRef}
      />
    );
  }

  return null;
}
